import React from "react";

import { reviewOutcomeLabel } from "@/components/shared/governance-labels";
import { Badge, countLabel, EmptyState, Heading, ListRow, Text, Time } from "@/components/ui";
import type { ProgrammeReviewRoundTimeline } from "@/platform";
import type { ProgrammeReviewOutcome, WorkflowState } from "@/server/types";
import { isWorkflowEditable } from "@/server/workflow";

export interface ReviewRoundTimelineProps {
  reviewRounds: ProgrammeReviewRoundTimeline[];
  /** Where the programme stands, which decides whether the last round still counts. */
  workflowState: WorkflowState;
  /** A reviewer's name, from their user id. */
  nameFor: (userId: string) => string;
}

/** A reviewer asked for changes, so the round waits on the lead, not on approvals. */
function sentBack(timeline: ProgrammeReviewRoundTimeline): boolean {
  return timeline.requiredReviewerStatuses.some(
    (status) => status.latestOutcome === "change-request",
  );
}

function roundStatus(
  timeline: ProgrammeReviewRoundTimeline,
  supersededBy: number | undefined,
  workflowState: WorkflowState,
): { tone: "neutral" | "success" | "warning"; label: string } {
  if (supersededBy !== undefined) {
    return { tone: "neutral", label: `Superseded by round ${supersededBy}` };
  }
  if (sentBack(timeline)) {
    return { tone: "warning", label: "Changes requested" };
  }
  // Editable again without a change request: only a return to Draft does that.
  if (isWorkflowEditable(workflowState)) {
    return { tone: "neutral", label: "Closed by return to Draft" };
  }
  return timeline.allRequiredApproved
    ? { tone: "success", label: "All required reviewers approved" }
    : { tone: "warning", label: "Approvals pending" };
}

/** What a reviewer wrote with each outcome, oldest first. */
function ReviewerNotes({
  outcomes,
  testIdPrefix,
}: {
  outcomes: readonly ProgrammeReviewOutcome[];
  testIdPrefix: string;
}): React.ReactElement | null {
  const noted = outcomes
    .filter((outcome) => outcome.note?.trim())
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  if (noted.length === 0) {
    return null;
  }
  return (
    <ul className="mt-1 space-y-1 ps-3">
      {noted.map((outcome) => {
        const accepted = outcome.openBlockerCount ?? 0;
        return (
          <li key={outcome.id} data-testid={`${testIdPrefix}-${outcome.id}`}>
            <Text as="span" size="sm" tone="muted">
              {reviewOutcomeLabel(outcome.outcome)}
              {accepted > 0 ? ` with ${countLabel(accepted, "open blocker")}` : ""} ·{" "}
              <Time value={outcome.createdAt} />
            </Text>
            <p className="whitespace-pre-line">{outcome.note}</p>
          </li>
        );
      })}
    </ul>
  );
}

export function ReviewRoundTimeline({
  reviewRounds,
  workflowState,
  nameFor,
}: ReviewRoundTimelineProps): React.ReactElement {
  if (reviewRounds.length === 0) {
    // Not absent: every reviewer action is refused with "Only a reviewer named
    // in the current review round can record an outcome", and a blank here
    // leaves that unanswerable — a reviewer cannot tell a round they are off
    // from a round nobody has opened.
    return (
      <section className="mt-4">
        <EmptyState
          icon="clipboard-text"
          title="No review round has been opened"
          description="A round opens when the programme is submitted for internal review, and names the reviewers who may record an outcome."
          data-testid="review-round-timeline-empty"
        />
      </section>
    );
  }

  return (
    <section className="mt-4" data-testid="review-round-timeline">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Heading level={3} size="md">
            Review round timeline
          </Heading>
          <Text tone="muted" className="mt-1">
            See the reviewer confirmations for each round, recorded at submission.
          </Text>
        </div>
        <Badge variant="outline" data-testid="review-round-count">
          {countLabel(reviewRounds.length, "round")}
        </Badge>
      </div>

      <div className="mt-3 space-y-3">
        {reviewRounds.map((timeline, index) => {
          // Rounds arrive in order; a later one means a resubmission replaced this.
          const supersededBy = reviewRounds[index + 1]?.round.roundNumber;
          const roundState = roundStatus(timeline, supersededBy, workflowState);
          return (
            <ListRow
              key={timeline.round.id}
              data-testid={`review-round-${timeline.round.roundNumber}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="primary">Round {timeline.round.roundNumber}</Badge>
                <Text as="span" size="sm" tone="muted">
                  Candidate revision {timeline.round.programmeRevision}
                </Text>
                <Badge
                  tone={roundState.tone}
                  data-testid={`review-round-status-${timeline.round.roundNumber}`}
                >
                  {roundState.label}
                </Badge>
              </div>

              <ul
                className="mt-3 space-y-2 text-md"
                data-testid={`review-round-reviewers-${timeline.round.roundNumber}`}
              >
                {timeline.requiredReviewerStatuses.map((status) => (
                  <li
                    key={`${timeline.round.id}-${status.userId}`}
                    data-testid={`review-round-reviewer-${timeline.round.roundNumber}-${status.userId}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">{nameFor(status.userId)}</span>
                      <Text as="span" size="sm" tone="muted">
                        {status.latestOutcome ? (
                          <>
                            {reviewOutcomeLabel(status.latestOutcome)}
                            {status.reviewedAt ? (
                              <>
                                {" · "}
                                <Time value={status.reviewedAt} />
                              </>
                            ) : null}
                          </>
                        ) : (
                          "Pending"
                        )}
                      </Text>
                    </div>
                    <ReviewerNotes
                      outcomes={(timeline.outcomes ?? []).filter(
                        (outcome) => outcome.reviewerId === status.userId,
                      )}
                      testIdPrefix={`review-round-note-${timeline.round.roundNumber}`}
                    />
                  </li>
                ))}
              </ul>

              {roundState.tone === "warning" &&
              !sentBack(timeline) &&
              timeline.missingReviewerUserIds.length > 0 ? (
                <Text
                  size="sm"
                  className="mt-3 text-warning-strong"
                  data-testid={`review-round-missing-${timeline.round.roundNumber}`}
                >
                  Missing approvals: {timeline.missingReviewerUserIds.map(nameFor).join(", ")}
                </Text>
              ) : null}
            </ListRow>
          );
        })}
      </div>
    </section>
  );
}
